import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { CustomMessageService } from '../shared/services/customPriemngMessage.service';
import { getTooManyRequestsMessage, isTooManyRequests } from '../shared/utils/tooManyRequestsMessage';

/** A burst of rate-limited requests should show one message, not one per request */
const TOO_MANY_REQUESTS_TOAST_GAP_MS = 5000;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private router = inject( Router );
  private auth = inject( AuthService );
  private messageService = inject( CustomMessageService );
  private lastTooManyRequestsToastAt = 0;


  intercept ( req: HttpRequest<any>, next: HttpHandler ): Observable<HttpEvent<any>> {
    return next.handle( req ).pipe(
      catchError( ( error: HttpErrorResponse ) => {
        const skipRedirect = req.headers.get( 'Skip-403-Redirect' ) === 'true';

        // Skip-Error-Toast: the caller shows the error itself (getSendOTPErrorMessage covers 429 too)
        if ( isTooManyRequests( error ) && req.headers.get( 'Skip-Error-Toast' ) !== 'true' ) {
          this.showTooManyRequests( error );
        }
        if ( error.status === 401 ) {
          this.auth.signOutUser();
        }
        if ( error.status === 403 && !skipRedirect ) {
          const apiUrl = req.url;
          const backendDescription = error?.error?.message || '';
          // A tenant-mismatch rejection means this tab's own resolved identity is invalid, not
          // just that the requested resource is off-limits - clear local auth state too, or
          // guards keep retrying the same failing request against a session that never clears.
          // Matched on the backend's machine-readable errorName, not the message text, so a
          // reworded message can't silently fall through to the generic /access-denied path.
          if ( error?.error?.errorName === 'TenantMismatchError' ) {
            this.auth.signOutUserLocally();
          } else {
            this.router.navigate( ['/access-denied'], { queryParams: { attemptedUrl: apiUrl, backendDescription } } );
          }
        }
        return throwError( () => error );
      } )
    );
  }

  /** Tells the user how long to wait, from the server's Retry-After header (seconds) */
  private showTooManyRequests ( error: HttpErrorResponse ): void {
    const now = Date.now();
    if ( now - this.lastTooManyRequestsToastAt < TOO_MANY_REQUESTS_TOAST_GAP_MS ) {
      return;
    }
    this.lastTooManyRequestsToastAt = now;

    this.messageService.error( {
      title: 'Please slow down',
      message: getTooManyRequestsMessage( error ),
      keepPrevious: true,
    } );
  }
}
